import {
  buildReferralAuthOnFileMessage,
  type ReferralAuthOnFileSnapshot,
} from "@/src/lib/prior-authorization/referral-auth-on-file";
import { classifyReferralAuthNeed } from "@/src/lib/prior-authorization/referral-auth-status";
import { ReferralAuthorizationTeStatus } from "@/src/models/referral-authorization";

const LOADING_MESSAGE =
  "Stedi eligibility check in progress. Review authorization instructions once results are available.";
const PENDING_MESSAGE =
  "Stedi eligibility check is pending or unavailable. Verify authorization requirement manually before scheduling.";
const UNKNOWN_MESSAGE =
  "Unable to determine authorization requirement from Stedi results. Verify manually before scheduling.";
const REQUIRED_FAX_ECW_MESSAGE =
  "Referral authorization is required. Valerie Health is sending an outbound request to the PCP to obtain authorization.";
const NOT_REQUIRED_STEDI_MESSAGE = "No referral authorization is required based on Stedi eligibility results.";

export function formatDefinitiveStediReferralAuthLine(officeVisitRequiresAuth: boolean | null): string | null {
  if (officeVisitRequiresAuth === true) return REQUIRED_FAX_ECW_MESSAGE;
  if (officeVisitRequiresAuth === false) return NOT_REQUIRED_STEDI_MESSAGE;

  return null;
}

export function buildAutonomyReferralAuthInstruction({
  isLoading,
  hasEligibilityCheck,
  teStatus,
  planType,
  authOnFile,
}: {
  isLoading: boolean;
  hasEligibilityCheck: boolean;
  teStatus: ReferralAuthorizationTeStatus | null;
  planType: string | null;
  authOnFile: ReferralAuthOnFileSnapshot;
}): string {
  if (authOnFile.authorizationNumber) {
    return authOnFile.hasAuthOnFile
      ? buildReferralAuthOnFileMessage(authOnFile, "Referral authorization is on file.")
      : buildReferralAuthOnFileMessage(
          authOnFile,
          "Authorization number is on file; confirm validity dates and visits before scheduling."
        );
  }

  if (isLoading) return LOADING_MESSAGE;
  if (!hasEligibilityCheck) return PENDING_MESSAGE;

  const need = classifyReferralAuthNeed(teStatus, planType);

  const stediLine = formatDefinitiveStediReferralAuthLine(
    need === "required" ? true : need === "not_required" ? false : null
  );

  return stediLine ?? UNKNOWN_MESSAGE;
}

export function canStartReferralAuthTrackingCase({
  isLoading,
  hasEligibilityCheck,
  teStatus,
  planType,
}: {
  isLoading: boolean;
  hasEligibilityCheck: boolean;
  teStatus: ReferralAuthorizationTeStatus | null;
  planType: string | null;
}): boolean {
  if (isLoading) return false;
  if (!hasEligibilityCheck) return true;

  return classifyReferralAuthNeed(teStatus, planType) !== "not_required";
}

export function getReferralAuthTrackingCaseButtonTooltip({
  isLoading,
  hasEligibilityCheck,
  teStatus,
  planType,
  isSubmitting,
}: {
  isLoading: boolean;
  hasEligibilityCheck: boolean;
  teStatus: ReferralAuthorizationTeStatus | null;
  planType: string | null;
  isSubmitting?: boolean;
}): string {
  if (isSubmitting) {
    return "Finish updating the task before opening authorization tracking.";
  }

  if (isLoading) {
    return "Waiting for Stedi eligibility results.";
  }

  if (hasEligibilityCheck && classifyReferralAuthNeed(teStatus, planType) === "not_required") {
    return "Authorization tracking is not needed based on Stedi eligibility results.";
  }

  return "Opens the authorization in the Authorizations tab to track status and outreach.";
}
